import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Download, PenLine, RotateCcw } from "lucide-react";

const SHAPES = {
  Almond: "M10,210 C8,130 18,50 45,8 C72,50 82,130 80,210 Z",
  Coffin: "M8,210 C12,140 20,70 30,30 Q32,20 40,20 L50,20 Q58,20 60,30 C70,70 78,140 82,210 Z",
  Oval: "M10,210 C6,120 12,50 45,14 C78,50 84,120 80,210 Z",
  Round: "M6,210 L6,90 C6,40 20,14 45,14 C70,14 84,40 84,90 L84,210 Z",
  Square: "M6,210 L6,26 Q6,16 16,16 L74,16 Q84,16 84,26 L84,210 Z",
  Squoval: "M6,210 L6,44 Q6,14 45,14 Q84,14 84,44 L84,210 Z",
  Stiletto: "M14,210 C12,140 22,60 45,6 C68,60 78,140 76,210 Z",
};

const PALETTE = [
  { name: "Ballet Blush", hex: "#F2D8D3", slug: "ballet-blush" },
  { name: "Lilac Whisper", hex: "#DDD3E8", slug: "lilac-whisper" },
  { name: "Crème de Rose", hex: "#E8BCB2", slug: "creme-de-rose" },
  { name: "Buttermilk", hex: "#F4EBD9", slug: "buttermilk" },
  { name: "Pistachio Crème", hex: "#D8E2D2", slug: "pistachio-creme" },
  { name: "Powder Blue", hex: "#CFDDE8", slug: "powder-blue" },
  { name: "Peach Sorbet", hex: "#F3D2BE", slug: "peach-sorbet" },
  { name: "Porcelain", hex: "#F6F1EA", slug: "porcelain" },
  { name: "Noir", hex: "#26221F", slug: "noir" },
];

const PLACEMENTS = [
  { id: "all", label: "All Fingers" },
  { id: 0, label: "Thumb" },
  { id: 1, label: "Index" },
  { id: 2, label: "Middle" },
  { id: 3, label: "Ring" },
  { id: 4, label: "Pinky" },
];

const FONTS = [
  { name: "Italiana", label: "Milano", family: "'Italiana', sans-serif", slug: "italiana" },
  { name: "Abril Fatface", label: "Éditorial", family: "'Abril Fatface', serif", slug: "abril" },
  { name: "Cinzel Decorative", label: "Deco Luxe", family: "'Cinzel Decorative', serif", slug: "cinzel" },
  { name: "Monoton", label: "Neon", family: "'Monoton', cursive", slug: "monoton" },
  { name: "Megrim", label: "Avant", family: "'Megrim', sans-serif", slug: "megrim" },
  { name: "Pinyon Script", label: "Calligraphy", family: "'Pinyon Script', cursive", slug: "pinyon" },
  { name: "Graduate", label: "Varsity", family: "'Graduate', serif", slug: "graduate" },
];

const CHARMS = [
  {
    slug: "star",
    label: "Star",
    draw: (c) => <path d="M20,5 L24.5,15.5 L36,16 L27,23.5 L30.5,34.5 L20,28 L9.5,34.5 L13,23.5 L4,16 L15.5,15.5 Z" fill={c} />,
  },
  {
    slug: "heart",
    label: "Heart",
    draw: (c) => <path d="M20,33 C9,25 5,17 8.5,11.5 C11.5,7 17.5,7.5 20,12.5 C22.5,7.5 28.5,7 31.5,11.5 C35,17 31,25 20,33 Z" fill={c} />,
  },
  {
    slug: "moon",
    label: "Moon",
    draw: (c) => <path d="M25,5 C16.5,7 10.5,14 10.5,21.5 C10.5,29.5 17,35.5 24.5,35.5 C27,35.5 29.5,34.8 31.5,33.5 C24,31.8 18.5,26 18.5,19.5 C18.5,13.5 21.5,8.5 25,5 Z" fill={c} />,
  },
  {
    slug: "bird",
    label: "Bird",
    draw: (c) => (
      <path d="M5,17 C11,9 17,9 20,15 C23,9 29,9 35,17 C29,13.5 24,13.5 20,19 C16,13.5 11,13.5 5,17 Z" fill="none" stroke={c} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    ),
  },
  {
    slug: "butterfly",
    label: "Butterfly",
    draw: (c) => (
      <g>
        <path d="M20,19 C15,7 5,7 7.5,15 C9,19.5 14.5,20.5 19,20 C15,22 10.5,24 12,29.5 C13.5,35 19,30 20,23.5 C21,30 26.5,35 28,29.5 C29.5,24 25,22 21,20 C25.5,20.5 31,19.5 32.5,15 C35,7 25,7 20,19 Z" fill={c} />
        <line x1="20" y1="18" x2="20" y2="27" stroke={c} strokeWidth="2" />
      </g>
    ),
  },
  {
    slug: "flower",
    label: "Flower",
    draw: (c) => (
      <g>
        {[0, 60, 120, 180, 240, 300].map((a) => (
          <ellipse key={a} cx="20" cy="11" rx="4.5" ry="7" fill={c} opacity="0.85" transform={`rotate(${a} 20 20)`} />
        ))}
        <circle cx="20" cy="20" r="4" fill={c} />
      </g>
    ),
  },
  {
    slug: "sparkle",
    label: "Sparkle",
    draw: (c) => <path d="M20,4 L23.5,16.5 L36,20 L23.5,23.5 L20,36 L16.5,23.5 L4,20 L16.5,16.5 Z" fill={c} />,
  },
  {
    slug: "crown",
    label: "Crown",
    draw: (c) => <path d="M7,29 L9,13 L15.5,20 L20,9 L24.5,20 L31,13 L33,29 Z" fill={c} />,
  },
];

const FINGER_SCALE = [
  { w: 1.12, h: 0.88 },
  { w: 0.98, h: 1.0 },
  { w: 1.0, h: 1.06 },
  { w: 0.96, h: 1.0 },
  { w: 0.8, h: 0.85 },
];

const hsvToHex = (h, s, v) => {
  s /= 100;
  v /= 100;
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  const [r, g, b] =
    h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  const to = (n) => Math.round((n + m) * 255).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase();
};

const hexToHsv = (hex) => {
  const n = parseInt(hex.replace("#", ""), 16);
  if (Number.isNaN(n)) return { h: 0, s: 0, v: 90 };
  const r = (n >> 16) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d) {
    if (max === r) h = 60 * (((g - b) / d) % 6);
    else if (max === g) h = 60 * ((b - r) / d + 2);
    else h = 60 * ((r - g) / d + 4);
  }
  if (h < 0) h += 360;
  return { h: Math.round(h), s: Math.round((max ? d / max : 0) * 100), v: Math.round(max * 100) };
};

const mixHex = (hex, target, t) => {
  const a = parseInt(hex.slice(1), 16);
  const b = parseInt(target.slice(1), 16);
  if (Number.isNaN(a) || Number.isNaN(b)) return hex;
  const mix = (sa, sb) => Math.round(sa + (sb - sa) * t);
  const r = mix(a >> 16, b >> 16);
  const g = mix((a >> 8) & 255, (b >> 8) & 255);
  const bl = mix(a & 255, b & 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + bl).toString(16).slice(1).toUpperCase()}`;
};

function ColorField({ value, onChange }) {
  const fieldRef = useRef(null);
  const hueRef = useRef(null);
  const hsv = hexToHsv(value);

  const drag = (ref, fn) => (e) => {
    const move = (ev) => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width));
      const y = Math.min(1, Math.max(0, (ev.clientY - rect.top) / rect.height));
      fn(x, y);
    };
    move(e);
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div className="w-full max-w-xs">
      <div
        ref={fieldRef}
        onPointerDown={drag(fieldRef, (x, y) => onChange(hsvToHex(hsv.h, Math.round(x * 100), Math.round((1 - y) * 100))))}
        className="relative h-40 w-full cursor-crosshair touch-none border border-[#26221F]/20"
        style={{
          background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hsv.h}, 100%, 50%))`,
        }}
        data-testid="color-field"
        role="slider"
        aria-label="Colour graph — drag to choose saturation and depth"
      >
        <span
          className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
          style={{ left: `${hsv.s}%`, top: `${100 - hsv.v}%`, background: value }}
        />
      </div>
      <div
        ref={hueRef}
        onPointerDown={drag(hueRef, (x) => onChange(hsvToHex(Math.round(x * 360), hsv.s, hsv.v)))}
        className="relative mt-3 h-3 w-full cursor-pointer touch-none border border-[#26221F]/20"
        style={{ background: "linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)" }}
        data-testid="hue-slider"
        role="slider"
        aria-label="Hue slider"
      >
        <span
          className="pointer-events-none absolute top-1/2 h-5 w-2 -translate-x-1/2 -translate-y-1/2 border border-white shadow"
          style={{ left: `${(hsv.h / 360) * 100}%`, background: `hsl(${hsv.h}, 100%, 50%)` }}
        />
      </div>
    </div>
  );
}

export default function CustomStudio() {
  const [shape, setShape] = useState("Almond");
  const [nailHex, setNailHex] = useState("#F2D8D3");
  const [monoHex, setMonoHex] = useState("#C6A15B");
  const [target, setTarget] = useState("nail");
  const [monoText, setMonoText] = useState("");
  const [placement, setPlacement] = useState("all");
  const [font, setFont] = useState(FONTS[6]);
  const [monoOffset, setMonoOffset] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);

  const rawLetters = monoText.replace(/[^a-zA-Z]/g, "").slice(0, 5).split("");
  const letters =
    font.slug === "pinyon"
      ? rawLetters.map((c, i) => (i === 0 ? c.toUpperCase() : c.toLowerCase()))
      : rawLetters.map((c) => c.toUpperCase());

  const [bold, setBold] = useState(false);
  const [emboss, setEmboss] = useState(false);
  const [charm, setCharm] = useState(null);
  const [charmHex, setCharmHex] = useState("#C6A15B");
  const [charmPlace, setCharmPlace] = useState(3);

  const nudge = (dx, dy) =>
    setMonoOffset((o) => ({
      x: Math.min(30, Math.max(-30, o.x + dx)),
      y: Math.min(50, Math.max(-50, o.y + dy)),
    }));

  const letterEls = (x, y, ch, size, key) => {
    const common = {
      x,
      y,
      textAnchor: "middle",
      fontFamily: font.family,
      fontSize: size,
      fontWeight: bold ? 700 : 400,
    };
    if (!emboss)
      return (
        <text key={key} {...common} fill={monoHex} stroke="#26221F" strokeOpacity="0.12" strokeWidth={bold ? 1.6 : 1}>
          {ch}
        </text>
      );
    return (
      <g key={key}>
        <text {...common} y={y + 2} fill={mixHex(monoHex, "#26221F", 0.5)}>
          {ch}
        </text>
        <text {...common} y={y - 1.6} fill="#FFFFFF" opacity="0.8">
          {ch}
        </text>
        <text {...common} fill={monoHex}>
          {ch}
        </text>
      </g>
    );
  };
  const monoStart = letters.length ? Math.round((5 - letters.length) / 2) : -1;
  const activeHex = target === "nail" ? nailHex : target === "charm" ? charmHex : monoHex;
  const applyColor = (hex) =>
    target === "nail" ? setNailHex(hex) : target === "charm" ? setCharmHex(hex) : setMonoHex(hex);
  const nailName =
    PALETTE.find((p) => p.hex.toUpperCase() === nailHex.toUpperCase())?.name || `Custom ${nailHex}`;
  const placementLabel = PLACEMENTS.find((p) => p.id === placement)?.label || "All Fingers";

  const saveDesign = () => {
    const svg = svgRef.current;
    if (!svg) return;
    const data = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 1120;
      canvas.height = 520;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FAF7F2";
      ctx.fillRect(0, 0, 1120, 520);
      ctx.drawImage(img, 0, 0, 1120, 520);
      const a = document.createElement("a");
      a.download = `her-label-${shape.toLowerCase()}-bespoke.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
    };
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(data);
  };

  return (
    <section
      id="customise"
      className="border-t border-[#26221F]/15 bg-[#F3E6E1]/40 px-6 py-24 lg:px-12 lg:py-32"
      data-testid="customise-section"
    >
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 max-w-2xl"
        >
          <p className="mb-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.4em] text-[#6F665F]">
            <PenLine className="h-4 w-4 text-[#D8AFA4]" /> The Custom Studio
          </p>
          <h2 className="font-display text-4xl leading-none tracking-tight md:text-6xl" data-testid="customise-heading">
            Design a label of <em className="italic text-[#D8AFA4]">your</em> own
          </h2>
          <p className="mt-6 max-w-lg text-sm leading-relaxed text-[#6F665F] md:text-base">
            Choose your shape, mix any colour on the colour graph, monogram one
            finger or all five — our artisans hand-paint every bespoke set to
            order.
          </p>
        </motion.div>
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative lg:col-span-4"
          >
            <div className="absolute -left-5 -top-5 h-full w-full border border-[#26221F]/15 bg-[#EAE5F0]" aria-hidden="true" />
            <div className="relative overflow-hidden border border-[#26221F]/20">
              <img
                src="/customise-nails.jpeg"
                alt="Hand-painted bespoke press-on nails with Mira monogram"
                loading="lazy"
                className="aspect-[4/5] w-full object-cover"
                data-testid="customise-image"
              />
              <span className="absolute left-4 top-4 rounded-full bg-[#FAF7F2]/90 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#26221F]">
                Coming Soon
              </span>
            </div>
            <p className="mt-6 font-display text-lg italic text-[#6F665F]">
              “Mira” — a bespoke set, lettered by hand.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-8"
          >
            <div className="border border-[#26221F]/20 bg-[#FAF7F2]">
              <div className="border-b border-[#26221F]/15 px-6 py-8 md:px-10">
                <svg ref={svgRef} viewBox="0 0 560 260" className="mx-auto w-full max-w-xl" data-testid="studio-preview" role="img" aria-label="Preview of your bespoke nail set">
                  <rect width="560" height="260" fill="#FAF7F2" />
                  {FINGER_SCALE.map((s, i) => (
                    <g key={i} transform={`translate(${34 + i * 102}, 240) scale(${s.w}, ${s.h}) translate(-45, -210)`}>
                      <path d={SHAPES[shape]} fill={nailHex} stroke="#26221F" strokeOpacity="0.25" strokeWidth="2" />
                      {placement === "all" &&
                        monoStart >= 0 &&
                        i >= monoStart &&
                        i < monoStart + letters.length &&
                        letterEls(45 + monoOffset.x, 130 + monoOffset.y, letters[i - monoStart], 38, `a${i}`)}
                      {placement !== "all" &&
                        i === placement &&
                        letters.map((ch, li) =>
                          letterEls(45 + monoOffset.x, 68 + li * 30 + monoOffset.y, ch, 28, `s${li}`)
                        )}
                      {charm &&
                        (charmPlace === "all" || i === charmPlace) && (
                          <g transform="translate(25, 96)" data-testid={`charm-on-nail-${i}`}>
                            {CHARMS.find((c) => c.slug === charm)?.draw(charmHex)}
                          </g>
                        )}
                    </g>
                  ))}
                </svg>
                <p className="mt-4 text-center text-[10px] font-semibold uppercase tracking-[0.3em] text-[#6F665F]" data-testid="studio-summary">
                  {shape} · {nailName}
                  {letters.length > 0 && ` · Monogram “${letters.join("")}” on ${placementLabel} · ${font.label} · Ink ${monoHex}`}
                  {charm && ` · ${CHARMS.find((c) => c.slug === charm)?.label} charm on ${charmPlace === "all" ? "all fingers" : PLACEMENTS.find((p) => p.id === charmPlace)?.label} · Ink ${charmHex}`}
                </p>
                {letters.length > 0 && (
                  <div className="mt-5 flex items-center justify-center gap-6" data-testid="monogram-nudge-pad">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#6F665F]">Nudge it</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      <span />
                      <button onClick={() => nudge(0, -6)} aria-label="Move monogram up" data-testid="mono-nudge-up" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#26221F]/25 text-[#26221F] transition-colors duration-300 hover:bg-[#26221F] hover:text-[#FAF7F2]">
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <span />
                      <button onClick={() => nudge(-6, 0)} aria-label="Move monogram left" data-testid="mono-nudge-left" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#26221F]/25 text-[#26221F] transition-colors duration-300 hover:bg-[#26221F] hover:text-[#FAF7F2]">
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button onClick={() => setMonoOffset({ x: 0, y: 0 })} aria-label="Reset monogram position" data-testid="mono-nudge-reset" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#26221F]/25 text-[#26221F] transition-colors duration-300 hover:bg-[#26221F] hover:text-[#FAF7F2]">
                        <RotateCcw className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => nudge(6, 0)} aria-label="Move monogram right" data-testid="mono-nudge-right" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#26221F]/25 text-[#26221F] transition-colors duration-300 hover:bg-[#26221F] hover:text-[#FAF7F2]">
                        <ChevronRight className="h-4 w-4" />
                      </button>
                      <span />
                      <button onClick={() => nudge(0, 6)} aria-label="Move monogram down" data-testid="mono-nudge-down" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#26221F]/25 text-[#26221F] transition-colors duration-300 hover:bg-[#26221F] hover:text-[#FAF7F2]">
                        <ChevronDown className="h-4 w-4" />
                      </button>
                      <span />
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-10 px-6 py-10 md:px-10">
                <div>
                  <p className="mb-5 text-xs font-semibold uppercase tracking-[0.35em] text-[#6F665F]">01 — Choose your shape</p>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(SHAPES).map(([name, d]) => (
                      <button
                        key={name}
                        onClick={() => setShape(name)}
                        data-testid={`shape-option-${name.toLowerCase()}`}
                        className={`flex flex-col items-center gap-2 border px-4 py-3 transition-colors duration-300 ${
                          shape === name
                            ? "border-[#26221F] bg-[#26221F] text-[#FAF7F2]"
                            : "border-[#26221F]/20 text-[#26221F] hover:bg-[#F3E6E1]"
                        }`}
                      >
                        <svg viewBox="30 0 30 210" className="h-10 w-4">
                          <path d={d} fill={shape === name ? "#D8AFA4" : "#26221F"} fillOpacity={shape === name ? 1 : 0.2} />
                        </svg>
                        <span className="text-[9px] font-semibold uppercase tracking-[0.2em]">{name}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-5 text-xs font-semibold uppercase tracking-[0.35em] text-[#6F665F]">
                    02 — Choose your colour, or make your own
                  </p>
                  <div className="mb-6 flex gap-3">
                    <button
                      onClick={() => setTarget("nail")}
                      data-testid="color-target-nail"
                      className={`rounded-full border px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.25em] transition-colors duration-300 ${
                        target === "nail"
                          ? "border-[#26221F] bg-[#26221F] text-[#FAF7F2]"
                          : "border-[#26221F]/25 text-[#26221F] hover:bg-[#F3E6E1]"
                      }`}
                    >
                      Nail Colour
                    </button>
                    <button
                      onClick={() => setTarget("monogram")}
                      data-testid="color-target-monogram"
                      className={`rounded-full border px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.25em] transition-colors duration-300 ${
                        target === "monogram"
                          ? "border-[#26221F] bg-[#26221F] text-[#FAF7F2]"
                          : "border-[#26221F]/25 text-[#26221F] hover:bg-[#F3E6E1]"
                      }`}
                    >
                      Monogram Ink
                    </button>
                    <button
                      onClick={() => setTarget("charm")}
                      data-testid="color-target-charm"
                      className={`rounded-full border px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.25em] transition-colors duration-300 ${
                        target === "charm"
                          ? "border-[#26221F] bg-[#26221F] text-[#FAF7F2]"
                          : "border-[#26221F]/25 text-[#26221F] hover:bg-[#F3E6E1]"
                      }`}
                    >
                      Charm
                    </button>
                  </div>
                  <div className="flex flex-wrap items-start gap-10">
                    <div>
                      <p className="mb-4 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#6F665F]">House shades</p>
                      <div className="flex max-w-xs flex-wrap gap-4">
                        {PALETTE.map((c) => (
                          <button
                            key={c.slug}
                            onClick={() => applyColor(c.hex)}
                            title={c.name}
                            aria-label={`Colour ${c.name}`}
                            data-testid={`color-option-${c.slug}`}
                            className={`h-11 w-11 rounded-full border transition-transform duration-300 hover:scale-110 ${
                              activeHex.toUpperCase() === c.hex.toUpperCase()
                                ? "ring-2 ring-[#26221F] ring-offset-2 ring-offset-[#FAF7F2]"
                                : ""
                            }`}
                            style={{ backgroundColor: c.hex, borderColor: "rgba(38,34,31,0.25)" }}
                          />
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="mb-4 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#6F665F]">
                        Make your own — drag on the colour graph
                      </p>
                      <ColorField value={activeHex} onChange={applyColor} />
                      <div className="mt-4 flex items-center gap-3">
                        <span
                          className="h-8 w-8 rounded-full border border-[#26221F]/25"
                          style={{ backgroundColor: activeHex }}
                          aria-hidden="true"
                        />
                        <input
                          value={activeHex}
                          onChange={(e) => {
                            const v = e.target.value.trim();
                            if (/^#?[0-9a-fA-F]{6}$/.test(v)) {
                              applyColor(v.startsWith("#") ? v.toUpperCase() : `#${v.toUpperCase()}`);
                            }
                          }}
                          aria-label="Custom colour hex code"
                          data-testid="hex-input"
                          className="w-28 border-b-2 border-[#26221F]/30 bg-transparent pb-1 font-mono text-sm uppercase tracking-widest text-[#26221F] focus:border-[#D8AFA4] focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-[#6F665F]" data-testid="studio-color-name">
                    {target === "nail" ? `Nail: ${nailName}` : target === "charm" ? `Charm: ${charmHex}` : `Monogram ink: ${monoHex}`}
                  </p>
                </div>
                <div>
                  <p className="mb-5 text-xs font-semibold uppercase tracking-[0.35em] text-[#6F665F]">03 — Monogram it (optional)</p>
                  <div className="flex flex-wrap items-center gap-6">
                    <input
                      value={monoText}
                      onChange={(e) => setMonoText(e.target.value)}
                      maxLength={5}
                      placeholder="Up to 5 letters"
                      aria-label="Monogram letters"
                      data-testid="monogram-input"
                      className="w-48 border-b-2 border-[#26221F]/30 bg-transparent pb-2 font-display text-xl italic tracking-[0.2em] text-[#26221F] placeholder:text-[#6F665F]/40 focus:border-[#D8AFA4] focus:outline-none"
                    />
                    <span
                      className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6F665F]"
                      data-testid="monogram-ink-indicator"
                    >
                      Ink
                      <span className="h-6 w-6 rounded-full border border-[#26221F]/25" style={{ backgroundColor: monoHex }} />
                    </span>
                  </div>
                  <p className="mb-4 mt-6 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#6F665F]">Place it on</p>
                  <div className="flex flex-wrap gap-3">
                    {PLACEMENTS.map((p) => (
                      <button
                        key={String(p.id)}
                        onClick={() => setPlacement(p.id)}
                        data-testid={`mono-place-${String(p.id).toLowerCase()}`}
                        className={`rounded-full border px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${
                          placement === p.id
                            ? "border-[#26221F] bg-[#26221F] text-[#FAF7F2]"
                            : "border-[#26221F]/25 text-[#26221F] hover:bg-[#F3E6E1]"
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <p className="mb-4 mt-6 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#6F665F]">Lettering style</p>
                  <div className="flex flex-wrap gap-3" data-testid="monogram-font-picker">
                    {FONTS.map((f) => (
                      <button
                        key={f.slug}
                        onClick={() => setFont(f)}
                        data-testid={`mono-font-${f.slug}`}
                        className={`flex w-20 flex-col items-center gap-1 border px-3 py-3 transition-colors duration-300 ${
                          font.slug === f.slug
                            ? "border-[#26221F] bg-[#26221F] text-[#FAF7F2]"
                            : "border-[#26221F]/20 text-[#26221F] hover:bg-[#F3E6E1]"
                        }`}
                      >
                        <span className="text-2xl leading-none" style={{ fontFamily: f.family }}>
                          Aa
                        </span>
                        <span className="text-[8px] font-semibold uppercase tracking-[0.15em]">{f.label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="mt-6 flex flex-wrap gap-3" data-testid="monogram-style-toggles">
                    <button
                      onClick={() => setBold((b) => !b)}
                      data-testid="mono-style-bold"
                      className={`rounded-full border px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
                        bold
                          ? "border-[#26221F] bg-[#26221F] text-[#FAF7F2]"
                          : "border-[#26221F]/25 text-[#26221F] hover:bg-[#F3E6E1]"
                      }`}
                    >
                      Bold
                    </button>
                    <button
                      onClick={() => setEmboss((v) => !v)}
                      data-testid="mono-style-emboss"
                      className={`rounded-full border px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${
                        emboss
                          ? "border-[#26221F] bg-[#26221F] text-[#FAF7F2]"
                          : "border-[#26221F]/25 text-[#26221F] hover:bg-[#F3E6E1]"
                      }`}
                    >
                      Embossed
                    </button>
                  </div>
                  <p className="mb-4 mt-8 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#6F665F]">Add a charm (optional)</p>
                  <div className="flex flex-wrap items-center gap-3" data-testid="charm-picker">
                    {CHARMS.map((c) => (
                      <button
                        key={c.slug}
                        onClick={() => setCharm((cur) => (cur === c.slug ? null : c.slug))}
                        data-testid={`charm-option-${c.slug}`}
                        className={`flex w-16 flex-col items-center gap-1 border px-2 py-3 transition-colors duration-300 ${
                          charm === c.slug ? "border-[#26221F] bg-[#26221F]" : "border-[#26221F]/20 hover:bg-[#F3E6E1]"
                        }`}
                      >
                        <svg viewBox="0 0 40 40" className="h-7 w-7">
                          {c.draw(charm === c.slug ? "#D8AFA4" : "#26221F")}
                        </svg>
                        <span className={`text-[8px] font-semibold uppercase tracking-[0.15em] ${charm === c.slug ? "text-[#FAF7F2]" : "text-[#26221F]"}`}>
                          {c.label}
                        </span>
                      </button>
                    ))}
                    {charm && (
                      <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6F665F]" data-testid="charm-ink-indicator">
                        Ink
                        <span className="h-6 w-6 rounded-full border border-[#26221F]/25" style={{ backgroundColor: charmHex }} />
                      </span>
                    )}
                  </div>
                  {charm && (
                    <>
                      <p className="mb-3 mt-5 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#6F665F]">Charm on</p>
                      <div className="flex flex-wrap gap-2.5">
                        {PLACEMENTS.map((p) => (
                          <button
                            key={String(p.id)}
                            onClick={() => setCharmPlace(p.id)}
                            data-testid={`charm-place-${String(p.id).toLowerCase()}`}
                            className={`rounded-full border px-4 py-2 text-[9px] font-semibold uppercase tracking-[0.15em] transition-colors duration-300 ${
                              charmPlace === p.id
                                ? "border-[#26221F] bg-[#26221F] text-[#FAF7F2]"
                                : "border-[#26221F]/25 text-[#26221F] hover:bg-[#F3E6E1]"
                            }`}
                          >
                            {p.label}
                          </button>
                        ))}
                      </div>
                      <p className="mt-3 text-xs text-[#6F665F]/70">
                        Charm colour: use the colour graph above with Charm selected.
                      </p>
                    </>
                  )}
                  <p className="mt-3 text-xs text-[#6F665F]/70">
                    All Fingers letters across the set; a single finger stacks them like “Mira”. Use the arrows to nudge your monogram into place.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-5 border-t border-[#26221F]/10 pt-8">
                  <button
                    onClick={saveDesign}
                    data-testid="save-design-button"
                    className="flex items-center gap-3 rounded-full bg-[#26221F] px-8 py-4 text-xs font-semibold uppercase tracking-[0.3em] text-[#FAF7F2] transition-colors duration-300 hover:bg-[#D8AFA4] hover:text-[#26221F]"
                  >
                    <Download className="h-4 w-4" /> Save My Design
                  </button>
                  <a
                    href="#waitlist"
                    data-testid="studio-waitlist-link"
                    className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#26221F] underline decoration-[#D8AFA4] decoration-2 underline-offset-8 transition-colors duration-300 hover:text-[#D8AFA4]"
                  >
                    Reserve it — join the waitlist <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
